import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { makeStyles, tokens } from '@fluentui/react-components';
import { StarFilled, StarRegular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  ratingContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalS,
  },
  ratingText: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
    marginRight: tokens.spacingHorizontalXS,
  },
  ratingDisplay: {
    display: 'flex',
    gap: tokens.spacingHorizontalXXS,
  },
  starContainer: {
    position: 'relative',
    width: tokens.spacingHorizontalL,
    height: tokens.spacingVerticalL,
  },
  star: {
    width: tokens.spacingHorizontalL,
    height: tokens.spacingVerticalL,
    color: '#FF3400',
  },
  partialStar: {
    position: 'absolute',
    top: 0,
    left: 0,
    overflow: 'hidden',
  },
  commentText: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
    marginLeft: tokens.spacingHorizontalXS,
  },
});

export const Rating = ({
  i18n,
  ratingPoint,
  maxPoint,
  showRatingPoint,
  commentText,
}) => {
  const styles = useStyles();

  const renderStar = (index) => {
    const fillPercentage = Math.max(0, Math.min(100, (ratingPoint - index) * 100));
    const EmptyStar = <StarRegular className={styles.star} />;
    const FilledStar = <StarFilled className={styles.star} />;

    if (fillPercentage === 0) return <div className={styles.starContainer}>{EmptyStar}</div>;
    if (fillPercentage === 100) return <div className={styles.starContainer}>{FilledStar}</div>;

    return (
      <div className={styles.starContainer}>
        {EmptyStar}
        <div
          className={styles.partialStar}
          style={{ width: `${fillPercentage}%` }}
        >
          {FilledStar}
        </div>
      </div>
    );
  };

  return (
    <div className={classNames(styles.ratingContainer, 'rating-container')}>
      {showRatingPoint && (
        <div className={classNames(styles.ratingText, 'rating-text')}>
          {`${ratingPoint.toFixed(1)}/${maxPoint.toString()}`}
        </div>
      )}
      <div
        className={classNames(styles.ratingDisplay, 'rating-display')}
        aria-label={i18n.getString(_TL_('Rating value is {{ratingPoint}} of {{maxPoint}}'), { ratingPoint, maxPoint })}
        role="img"
      >
        {Array.from({ length: maxPoint }, (_, index) => (
          <React.Fragment key={index}>
            {renderStar(index)}
          </React.Fragment>
        ))}
      </div>
      {commentText && (
        <div className={classNames(styles.commentText, 'comment-text')}>
          {commentText}
        </div>
      )}
    </div>
  );
};

Rating.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  ratingPoint: PropTypes.number,
  maxPoint: PropTypes.number,
  showRatingPoint: PropTypes.bool,
  commentText: PropTypes.string,
};

Rating.defaultProps = {
  ratingPoint: 5,
  maxPoint: 5,
  showRatingPoint: false,
  commentText: undefined,
};
